/**
 * Store Zustand para gerenciamento de estado das indicações e geo_pins.
 *
 * Centraliza estado global: pins no mapa, indicação selecionada, filtros,
 * paginação, estatísticas e status de loading/erro.
 *
 * @module store/indicacoesSlice
 */

import { create } from "zustand";
import {
  createManualAddress,
  deleteAddress,
  fetchGeoPins,
  fetchIndicacaoById,
  fetchIndicacoes,
  fetchReviewAddresses,
  fetchReviewIndicacoes,
  fetchStats,
  triggerSync,
  updateManualAddress,
} from "../services/api.js";

/**
 * @typedef {Object} GeoPin
 * @property {number} id
 * @property {number} indicacao_id
 * @property {number|null} endereco_id
 * @property {string} raw_address
 * @property {string|null} normalized_address
 * @property {number|null} latitude
 * @property {number|null} longitude
 * @property {string} status
 * @property {string|null} geocoder_source
 * @property {boolean} is_approximate
 * @property {string} created_at
 * @property {string} updated_at
 */

/**
 * @typedef {Object} EnderecoExtraido
 * @property {number} id
 * @property {number} indicacao_id
 * @property {string} raw_text
 * @property {string} street
 * @property {string|null} number
 * @property {string|null} bairro
 * @property {string} source
 * @property {string} status
 * @property {number} geocode_attempts
 * @property {string|null} last_geocode_error
 * @property {GeoPin|null} geo_pin
 * @property {string} created_at
 * @property {string} updated_at
 */

/**
 * @typedef {Object} Indicacao
 * @property {number} id
 * @property {number} sapl_id
 * @property {number} numero
 * @property {number} ano
 * @property {string} ementa
 * @property {string|null} autor
 * @property {string|null} data_apresentacao
 * @property {string} created_at
 * @property {string} updated_at
 * @property {GeoPin[]} geo_pins
 * @property {EnderecoExtraido[]} enderecos_extraidos
 */

/**
 * @typedef {Object} PaginatedResponse
 * @property {Indicacao[]} items
 * @property {number} total
 * @property {number} page
 * @property {number} page_size
 * @property {number} pages
 */

/**
 * @typedef {Object} Stats
 * @property {number} total_indicacoes
 * @property {number} total_pins
 * @property {number} pins_aproximados
 * @property {string[]} autores
 * @property {number[]} anos
 */

/**
 * @typedef {Object} Filters
 * @property {number[]} anos
 * @property {string|null} autor
 * @property {string|null} bairro
 * @property {number|null} bairro_id
 * @property {string|null} thematic_category
 * @property {string|null} search
 * @property {boolean|null} aproximado
 */

/** Estado inicial dos filtros */
const initialFilters = {
  anos: [],
  autor: null,
  bairro: null,
  bairro_id: null,
  thematic_category: null,
  search: null,
  aproximado: null,
};

/** Rastreador de versão de requisição para evitar race conditions no clique rápido */
let selectRequestId = 0;

/**
 * Constrói uma chave determinística para os filtros globais de contagem
 * (excluindo o filtro geográfico de bairro).
 *
 * @param {Partial<Filters>} [filters={}]
 * @returns {string}
 */
export function buildCityPinsFilterKey(filters = {}) {
  const sortedAnos = Array.isArray(filters.anos)
    ? [...filters.anos].map(Number).sort((a, b) => a - b)
    : [];
  return JSON.stringify({
    anos: sortedAnos,
    autor: filters.autor || null,
    thematic_category: filters.thematic_category || null,
    search: filters.search || null,
    aproximado: filters.aproximado === undefined ? null : filters.aproximado,
  });
}

/**
 * Store principal da aplicação.
 */
const useStore = create((set, get) => ({
  // ── Geo Pins (mapa) ────────────────────────────────────────────────────
  /** @type {GeoPin[]} */
  pins: [],
  /**
   * Pins geolocalizados do recorte global ANTES do filtro geográfico de bairro.
   * Mantido em cache na store para alimentar a camada coroplética dos 132 bairros
   * sem refazer fetch quando o usuário seleciona/alterna um bairro no mapa.
   * @type {GeoPin[]}
   */
  cityPins: [],
  /** @type {string|null} */
  cityPinsFilterKey: null,
  pinsLoading: false,
  /** @type {string|null} */
  pinsError: null,

  /**
   * Carrega pins geocodificados para o mapa, separando os filtros de visualização (`pins`)
   * dos filtros de contagem global (`cityPins`).
   */
  loadPins: async () => {
    set({ pinsLoading: true, pinsError: null });
    try {
      const state = get();
      const filters = state.filters;
      const globalParams = {
        anos: filters.anos,
        autor: filters.autor,
        thematic_category: filters.thematic_category,
        search: filters.search,
        aproximado: filters.aproximado,
      };
      const globalKey = buildCityPinsFilterKey(filters);
      const hasBairroFilter = Boolean(filters.bairro || filters.bairro_id);

      if (!hasBairroFilter) {
        const pins = await fetchGeoPins({
          ...globalParams,
          bairro: null,
          bairro_id: null,
        });
        set({
          pins,
          cityPins: pins,
          cityPinsFilterKey: globalKey,
          pinsLoading: false,
        });
        return;
      }

      // Quando há filtro de bairro ativo:
      // preserva `cityPins` em memória sem novo fetch se os filtros globais não mudaram.
      const hasCachedCityPins =
        state.cityPinsFilterKey === globalKey &&
        Array.isArray(state.cityPins) &&
        state.cityPins.length > 0;

      if (hasCachedCityPins) {
        const pins = await fetchGeoPins({
          ...globalParams,
          bairro: filters.bairro,
          bairro_id: filters.bairro_id,
        });
        set({ pins, pinsLoading: false });
      } else {
        const [pins, cityPins] = await Promise.all([
          fetchGeoPins({
            ...globalParams,
            bairro: filters.bairro,
            bairro_id: filters.bairro_id,
          }),
          fetchGeoPins({
            ...globalParams,
            bairro: null,
            bairro_id: null,
          }),
        ]);
        set({
          pins,
          cityPins,
          cityPinsFilterKey: globalKey,
          pinsLoading: false,
        });
      }
    } catch (error) {
      set({ pinsError: error.message, pinsLoading: false });
    }
  },

  // ── Indicações (listagem paginada) ─────────────────────────────────────
  /** @type {Indicacao[]} */
  indicacoes: [],
  indicacoesTotal: 0,
  indicacoesPage: 1,
  indicacoesPageSize: 20,
  indicacoesPages: 0,
  indicacoesLoading: false,
  /** @type {string|null} */
  indicacoesError: null,

  /**
   * Carrega uma página de indicações com os filtros ativos.
   *
   * @param {number} [page] - Página desejada (padrão: página atual).
   */
  loadIndicacoes: async (page) => {
    const state = get();
    const targetPage = page ?? state.indicacoesPage;

    set({ indicacoesLoading: true, indicacoesError: null });
    try {
      const data = await fetchIndicacoes({
        page: targetPage,
        pageSize: state.indicacoesPageSize,
        ...state.filters,
      });
      set({
        indicacoes: data.items,
        indicacoesTotal: data.total,
        indicacoesPage: data.page,
        indicacoesPages: data.pages,
        indicacoesLoading: false,
      });
    } catch (error) {
      set({ indicacoesError: error.message, indicacoesLoading: false });
    }
  },

  // ── Indicação selecionada (detalhe) ────────────────────────────────────
  /** @type {Indicacao|null} */
  selectedIndicacao: null,
  selectedLoading: false,
  /** @type {string|null} */
  selectedError: null,
  /** @type {boolean} */
  isGeometryVisible: false,
  /** @type {number} */
  focusMapTrigger: 0,

  selectIndicacao: async (id) => {
    // Se a mesma indicação já estiver selecionada, apenas recentraliza o mapa sem recomputar
    if (get().selectedIndicacao?.id === id) {
      set({ focusMapTrigger: Date.now() });
      return;
    }

    selectRequestId++;
    const currentReqId = selectRequestId;

    // 1. Atualização instantânea com latência zero (0ms):
    // Reúne dados já carregados no cliente (da lista paginada e dos pins no mapa)
    const state = get();
    const fromList = state.indicacoes.find((i) => i.id === id);
    const relatedPins = state.pins.filter((p) => p.indicacao_id === id);
    const pinSummary = relatedPins[0]?.indicacao;
    const base = fromList || pinSummary || {};

    const immediateIndicacao = {
      id,
      numero: base.numero ?? pinSummary?.numero ?? id,
      ano: base.ano ?? pinSummary?.ano ?? new Date().getFullYear(),
      ementa: base.ementa ?? pinSummary?.ementa ?? "",
      autor: base.autor ?? pinSummary?.autor ?? "",
      thematic_category:
        base.thematic_category ?? pinSummary?.thematic_category ?? relatedPins[0]?.thematic_category ?? "OUTROS",
      sapl_id: base.sapl_id ?? pinSummary?.sapl_id ?? null,
      data_apresentacao: base.data_apresentacao ?? pinSummary?.data_apresentacao ?? null,
      geo_pins: base.geo_pins?.length ? base.geo_pins : relatedPins,
      enderecos_extraidos: base.enderecos_extraidos?.length
        ? base.enderecos_extraidos
        : relatedPins.map((p) => ({
            id: p.endereco_id || p.id,
            indicacao_id: id,
            raw_text: p.normalized_address || p.raw_address || "Endereço identificado",
            geo_pin: p,
          })),
    };

    // Atualiza o estado imediatamente, sem nenhum delay no painel lateral.
    // Geometrias ricas (área/linha) permanecem desligadas até o usuário clicar em 'Focar'.
    set({
      selectedIndicacao: immediateIndicacao,
      selectedLoading: false,
      selectedError: null,
      isGeometryVisible: false,
      focusMapTrigger: Date.now(),
    });

    // 2. Busca detalhes completos na API em background (para dados enriquecidos se houver)
    try {
      const fullIndicacao = await fetchIndicacaoById(id);

      // GUARDA CONTRA RACE CONDITION:
      // Só atualiza se esta requisição ainda for a mais recente E o usuário não tiver selecionado outro item
      if (currentReqId === selectRequestId && get().selectedIndicacao?.id === id && fullIndicacao) {
        const fullPins = Array.isArray(fullIndicacao.geo_pins) ? [...fullIndicacao.geo_pins] : [];
        if (Array.isArray(fullIndicacao.enderecos_extraidos)) {
          for (const end of fullIndicacao.enderecos_extraidos) {
            if (end?.geo_pin && !fullPins.some((p) => p.id === end.geo_pin.id)) {
              fullPins.push(end.geo_pin);
            }
          }
        }

        set({
          selectedIndicacao: {
            ...immediateIndicacao,
            ...fullIndicacao,
            geo_pins: fullPins.length > 0 ? fullPins : immediateIndicacao.geo_pins,
            enderecos_extraidos: fullIndicacao.enderecos_extraidos?.length
              ? fullIndicacao.enderecos_extraidos
              : immediateIndicacao.enderecos_extraidos,
          },
          selectedLoading: false,
        });
      }
    } catch (error) {
      if (currentReqId === selectRequestId && !immediateIndicacao.ementa) {
        set({ selectedError: error.message, selectedLoading: false });
      }
    }
  },

  /**
   * Alterna ou força a exibição da camada de geometria vetorial rica (LineString, Polygon).
   *
   * @param {boolean} [forcedValue]
   */
  toggleGeometryVisible: (forcedValue) => {
    set((state) => ({
      isGeometryVisible:
        typeof forcedValue === "boolean" ? forcedValue : !state.isGeometryVisible,
      focusMapTrigger: Date.now(),
    }));
  },

  /**
   * Dispara um sinal para reenquadrar o mapa no pin ou geometria ativa.
   */
  triggerFocusMap: () => {
    const sel = get().selectedIndicacao;
    const hasRichGeom = Boolean(
      sel?.geo_pins?.some(
        (p) => p.geometry_geojson && p.geometry_type && p.geometry_type !== "Point" && p.geometry_type !== "MultiPoint"
      ) ||
      sel?.enderecos_extraidos?.some(
        (e) => e.geo_pin?.geometry_geojson && e.geo_pin?.geometry_type && e.geo_pin?.geometry_type !== "Point" && e.geo_pin?.geometry_type !== "MultiPoint"
      )
    );
    set({
      focusMapTrigger: Date.now(),
      ...(hasRichGeom ? { isGeometryVisible: true } : {}),
    });
  },

  /**
   * Limpa a indicação selecionada.
   */
  clearSelection: () => {
    selectRequestId++; // Invalida qualquer requisição assíncrona pendente
    set({
      selectedIndicacao: null,
      selectedError: null,
      isGeometryVisible: false,
      focusMapTrigger: 0,
    });
  },

  // ── Filtros ────────────────────────────────────────────────────────────
  /** @type {Filters} */
  filters: { ...initialFilters },

  /**
   * Atualiza um ou mais filtros e recarrega pins + indicações.
   *
   * @param {Partial<Filters>} newFilters - Filtros a atualizar.
   * @param {boolean} [preserveSelection=false] - Se true, não reseta a indicação selecionada.
   */
  setFilters: async (newFilters, preserveSelection = false) => {
    const merged = { ...get().filters, ...newFilters };
    set({
      filters: merged,
      indicacoesPage: 1,
      ...(preserveSelection ? {} : { selectedIndicacao: null, selectedError: null }),
    });

    // Recarrega dados com os novos filtros
    const state = get();
    await Promise.all([state.loadPins(), state.loadIndicacoes(1), state.loadStats(merged)]);
  },

  /**
   * Reseta todos os filtros ao estado inicial e recarrega dados.
   */
  resetFilters: async () => {
    set({
      filters: { ...initialFilters },
      indicacoesPage: 1,
      selectedIndicacao: null,
      selectedError: null,
    });
    const state = get();
    await Promise.all([state.loadPins(), state.loadIndicacoes(1), state.loadStats(initialFilters)]);
  },

  // ── Estatísticas ──────────────────────────────────────────────────────
  /** @type {Stats|null} */
  stats: null,
  statsLoading: false,
  /** @type {string|null} */
  statsError: null,

  /**
   * Carrega estatísticas do dashboard.
   *
   * @param {Partial<Filters>} [customFilters]
   */
  loadStats: async (customFilters) => {
    set({ statsLoading: true, statsError: null });
    try {
      const activeFilters = customFilters || get().filters;
      const stats = await fetchStats(activeFilters);
      set({ stats, statsLoading: false });
    } catch (error) {
      set({ statsError: error.message, statsLoading: false });
    }
  },

  // ── Revisão manual ─────────────────────────────────────────────────────
  /** @type {any[]} */
  reviewAddresses: [],
  /** @type {any[]} */
  reviewIndicacoes: [],
  reviewLoading: false,
  /** @type {string|null} */
  reviewError: null,

  /**
   * Carrega itens de revisão (endereços + indicações sem endereço).
   *
   * @param {string} [status] - Status dos endereços em revisão (padrão: todos pendentes de revisão).
   */
  loadReview: async (status = "") => {
    set({ reviewLoading: true, reviewError: null });
    try {
      const [addresses, indicacoes] = await Promise.all([
        fetchReviewAddresses(status ? { status } : {}),
        fetchReviewIndicacoes(),
      ]);
      set({ reviewAddresses: addresses, reviewIndicacoes: indicacoes, reviewLoading: false });
    } catch (error) {
      set({ reviewError: error.message, reviewLoading: false });
      throw error;
    }
  },

  /**
   * Cria endereço manual para uma indicação.
   *
   * @param {{ indicacao_id: number, street: string, number?: string|null, bairro?: string|null }} data
   */
  createReviewAddress: async (data) => {
    set({ reviewLoading: true, reviewError: null, cityPinsFilterKey: null });
    try {
      await createManualAddress(data);
      const state = get();
      await Promise.all([state.loadReview(), state.loadPins(), state.loadIndicacoes(), state.loadStats()]);
    } catch (error) {
      set({ reviewError: error.message, reviewLoading: false });
    }
  },

  /**
   * Atualiza endereço em revisão.
   *
   * @param {number} enderecoId
   * @param {{ street?: string, number?: string|null, bairro?: string|null }} payload
   */
  updateReviewAddress: async (enderecoId, payload) => {
    set({ reviewLoading: true, reviewError: null, cityPinsFilterKey: null });
    try {
      const updated = await updateManualAddress(enderecoId, payload);
      set((state) => ({
        reviewAddresses: state.reviewAddresses.map((addr) =>
          addr.id === enderecoId ? updated : addr
        ),
        reviewLoading: false,
      }));
      const state = get();
      await Promise.all([state.loadPins(), state.loadIndicacoes(), state.loadStats()]);
    } catch (error) {
      set({ reviewError: error.message, reviewLoading: false });
    }
  },

  /**
   * Remove endereço (falso positivo).
   *
   * @param {number} enderecoId
   */
  deleteReviewAddress: async (enderecoId) => {
    set({ reviewLoading: true, reviewError: null, cityPinsFilterKey: null });
    try {
      await deleteAddress(enderecoId);
      set((state) => ({
        reviewAddresses: state.reviewAddresses.filter((addr) => addr.id !== enderecoId),
        reviewLoading: false,
      }));
      const state = get();
      await Promise.all([state.loadPins(), state.loadIndicacoes(), state.loadStats()]);
    } catch (error) {
      set({ reviewError: error.message, reviewLoading: false });
    }
  },

  // ── Sincronização ─────────────────────────────────────────────────────
  syncing: false,
  /** @type {string|null} */
  syncMessage: null,

  /**
   * Dispara sincronização manual com o SAPL.
   */
  triggerSync: async () => {
    set({ syncing: true, syncMessage: null, cityPinsFilterKey: null });
    try {
      const result = await triggerSync();
      set({ syncing: false, syncMessage: result.message });
    } catch (error) {
      set({ syncing: false, syncMessage: `Erro: ${error.message}` });
    }
  },

  /**
   * Limpa o estado administrativo sensível.
   */
  clearAdminState: () => {
    set({
      reviewAddresses: [],
      reviewIndicacoes: [],
      reviewLoading: false,
      reviewError: null,
      syncing: false,
      syncMessage: null,
    });
  },
}));

export default useStore;
